import {DataStatus, Separator} from 'platform/components';
import {match} from 'ts-pattern';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCheckoutByIdQuery, useGetCheckoutLinkForServiceOrderQuery} from '@dms/api/sales';
import {useGetServiceOrderContractQuery, useGetServiceOrderInsuranceQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {BoschFleetIntegrationCard} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CustomerContractSection} from './CustomerContractSection';
import {DiscountSection} from './DiscountSection';
import {VehicleClassSection} from './VehicleClassSection';

interface DiscountsProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
  canEditDiscount: boolean;
  canEditCustomerContract: boolean;
  orderType: string | undefined | null;
}

export function Discounts(props: DiscountsProps) {
  const {
    data: serviceOrderInsurance,
    isFetching: isServiceOrderInsuranceFetching,
    isError: isServiceOrderInsuranceError,
  } = useGetServiceOrderInsuranceQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: props.orderType !== 'INS'}
  );
  const {
    data: serviceOrderContract,
    isFetching: isServiceOrderContractFetching,
    isError: isServiceOrderContractError,
  } = useGetServiceOrderContractQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: props.orderType !== 'CON'}
  );
  const {data: checkout} = useGetCheckoutLinkForServiceOrderQuery(
    {serviceOrderId: props.serviceOrderId},
    {refetchOnMountOrArgChange: true}
  );
  const {data: checkoutData} = useGetCheckoutByIdQuery(
    {checkoutId: checkout?.checkoutId ?? ''},
    {skip: !checkout?.checkoutId}
  );

  const isAssignCustomerContractDisabled = match(props.orderType)
    .with('CON', () => isNilOrEmpty(serviceOrderContract?.provider))
    .with('INS', () => isNilOrEmpty(serviceOrderInsurance?.insuranceCompany))
    .otherwise(() => false);

  const isLoading = isServiceOrderInsuranceFetching || isServiceOrderContractFetching;
  const isError = isServiceOrderInsuranceError || isServiceOrderContractError;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <CustomerContractSection
        orderId={props.serviceOrderId}
        serviceCaseId={props.serviceCaseId}
        isReadOnly={props.isReadOnly}
        isAssignDisabled={isAssignCustomerContractDisabled}
        canEditCustomerContract={props.canEditCustomerContract}
        data-testid={suffixTestId('customerContract', props)}
      />
      <Separator />
      <VehicleClassSection
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        isReadOnly={props.isReadOnly}
        data-testid={suffixTestId('vehicleClass', props)}
      />
      {checkoutData?.billingInformation?.customerInformation?.id && (
        <BoschFleetIntegrationCard
          paddingTop={4}
          cardVariant="inlineGrey"
          subTitle={i18n.t('general.labels.additionalInformation')}
          customerId={checkoutData.billingInformation?.customerInformation?.id}
        />
      )}
      <Separator />
      <DiscountSection
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        isReadOnly={props.isReadOnly}
        canEditDiscount={props.canEditDiscount}
        data-testid={suffixTestId('discounts', props)}
      />
    </DataStatus>
  );
}
